import type { Poll } from "./pollTypes";
import type { PollSuggestion } from "../../types/poll-suggestions";
import type { DatePollSuggestion } from "../ai/products/date/DatePollService";
import type { FormPollSuggestion } from "../ai/products/form/FormPollService";

/**
 * Build a minimal `Poll` from a freshly generated `PollSuggestion`.
 *
 * Used right after Gemini's first `generatePollFromText` to hydrate the
 * EditorState reducer (`REPLACE_POLL`) so subsequent user messages land on
 * the modification flow (IntentDetectionService / editPollFromText) instead
 * of triggering another from-scratch generation. Without this bridge,
 * `currentPoll` stays `null` and every modification stacks a new card with a
 * different title and dates on top of the previous one.
 */
export function pollFromSuggestion(suggestion: PollSuggestion): Poll {
  const nowIso = new Date().toISOString();
  const id = `ai-suggestion-${Date.now()}`;

  const base = {
    id,
    slug: id,
    title: suggestion.title,
    description: suggestion.description,
    status: "draft" as const,
    created_at: nowIso,
    updated_at: nowIso,
  };

  if (suggestion.type === "form") {
    return {
      ...base,
      type: "form",
      questions: (suggestion as FormPollSuggestion)
        .questions as unknown as Poll["questions"],
    };
  }

  return {
    ...base,
    type: "date",
    dates: (suggestion as DatePollSuggestion).dates,
    timeSlots: (suggestion as DatePollSuggestion)
      .timeSlots as unknown as Poll["timeSlots"],
    // Cadence/audience détectées par l'IA — portées sur le Poll en mémoire
    // (champs libres) pour pré-remplir l'intent bar du PollCreator.
    cadence: (suggestion as DatePollSuggestion).cadence,
    audience: (suggestion as DatePollSuggestion).audience,
    // Marqueur explicite : ce Poll vient d'une vraie génération IA (pas d'un
    // brouillon vide/magic-link). PollCreator s'appuie STRICTEMENT sur ce
    // flag pour afficher la ligne d'indice "Pré-rempli par l'IA" — un simple
    // titre par défaut ne doit jamais la déclencher (cf. faux positif #964).
    aiPrefilled: true,
  } as Poll;
}
